<template>
  <div :class="$style.container">
    <img
      :width="width"
      :height="height"
      :src="src"
      :alt="alt"
      @mouseenter="handleOver"
      @mousemove="handleMove"
      @mouseleave="handleOut"
      ref="img"
    />
    <div
      :class="[$style.cursorMask, maskClass]"
      :style="maskStyle"
      ref="mask"
    ></div>
    <div
      :class="[$style.zoomMask, zoomClass]"
      :style="zoomStyle"
      v-show="zoomShow"
    >
      <img
        :class="$style.zoomImg"
        :alt="alt"
        :id="id"
        :src="zoomSrc"
        :style="zoomImgStyle"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "ImageMagnifier",
  props: {
    alt: {
      type: String,
      default: ""
    },
    width: {
      default: "auto"
    },
    height: {
      default: "auto"
    },
    id: {
      type: String,
      default: ""
    },
    orientation: {
      validator: function(value) {
        return ["right", "left", "top", "bottom"].indexOf(value) !== -1;
      },
      default: "right"
    },
    src: {},
    zoomSrc: {},
    zoomClass: {},
    maskWidth: {
      default: 100
    },
    maskHeight: {
      default: 100
    },
    maskBgColor: {
      type: String,
      default: "#409eff"
    },
    maskOpacity: {
      default: 0.5
    },
    maskClass: {},
    delayIn: {
      type: Number,
      default: 0
    },
    delayOut: {
      type: Number,
      default: 0
    },
    scale: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      zoomShow: false,
      imgRect: "",
      maskRect: "",
      maskX: 0,
      maskY: 0,
      zoomImage: "",
      zoomLeft: "",
      zoomTop: "",
      zoomImgWidth: 0,
      zoomImgHeight: 0,
      zoomPosition: {
        x: 0,
        y: 0
      },
      zoomInTimeoutId: null,
      zoomOutTimeoutId: null
    };
  },
  computed: {
    maskStyle() {
      return {
        width: `${this.maskWidth}px`,
        height: `${this.maskHeight}px`,
        opacity: this.maskOpacity,
        backgroundColor: this.maskBgColor,
        left: `${this.maskX}px`,
        top: `${this.maskY}px`,
        visibility: this.zoomShow ? "visible" : "hidden"
      };
    },
    zoomStyle() {
      const imgRect = this.imgRect
        ? this.imgRect
        : this.$refs.img && this.$refs.img.getBoundingClientRect();
      const scaledMaskWidth =
        this.scale && imgRect ? this.maskWidth * this.scale : null;
      const scaledMaskHeight =
        this.scale && imgRect ? this.maskHeight * this.scale : null;
      return {
        width: scaledMaskWidth ? `${scaledMaskWidth}px` : "auto",
        height: scaledMaskHeight ? `${scaledMaskHeight}px` : "auto",
        bottom:
          this.orientation === "bottom"
            ? `-${scaledMaskHeight + 10}px`
            : "auto",
        left:
          this.orientation === "left"
            ? `-${scaledMaskWidth + 10}px`
            : this.orientation === "top" || this.orientation === "bottom"
            ? `-${(scaledMaskWidth - this.zoomLeft) / 2}px`
            : "auto",
        right:
          this.orientation === "right" || !this.orientation
            ? `-${scaledMaskWidth + 10}px`
            : "auto",
        top:
          this.orientation === "top"
            ? `-${scaledMaskHeight + 10}px`
            : this.orientation === "left" || this.orientation === "right"
            ? `-${(scaledMaskHeight - this.zoomTop) / 2}px`
            : "auto"
      };
    },
    zoomImgStyle() {
      return {
        width: `${this.zoomImgWidth}px`,
        height: `${this.zoomImgHeight}px`,
        left: `-${this.zoomPosition.x}px`,
        top: `-${this.zoomPosition.y}px`
      };
    }
  },
  mounted() {},
  methods: {
    handleOver() {
      clearTimeout(this.zoomOutTimeoutId);
      this.calcZoomSize();
      if (this.delayIn === 0) {
        this.zoomShow = true;
      } else {
        this.zoomInTimeoutId = setTimeout(() => {
          this.zoomShow = true;
        }, this.delayIn);
      }
    },
    calcZoomSize() {
      this.imgRect = this.$refs.img && this.$refs.img.getBoundingClientRect();
      this.maskRect =
        this.$refs.mask && this.$refs.mask.getBoundingClientRect();
      if (this.imgRect && this.maskRect) {
        this.zoomLeft = this.imgRect.width + 10;
        this.zoomTop = this.imgRect.height + 10;
        this.zoomImgWidth = this.imgRect.width * this.scale;
        this.zoomImgHeight = this.imgRect.height * this.scale;
      }
    },
    handleMove(e) {
      if (!this.imgRect || !this.maskRect) {
        return;
      }
      this.maskX = this.outXCheck(e.clientX - this.imgRect.left);
      this.maskY = this.outYCheck(e.clientY - this.imgRect.top);
      this.zoomPosition.x =
        this.maskX * (this.zoomImgWidth / this.imgRect.width);
      this.zoomPosition.y =
        this.maskY * (this.zoomImgHeight / this.imgRect.height);
    },
    handleOut() {
      clearTimeout(this.zoomInTimeoutId);
      if (this.delayOut === 0) {
        this.zoomShow = false;
      } else {
        this.zoomOutTimeoutId = setTimeout(() => {
          this.zoomShow = false;
        }, this.delayOut);
      }
    },
    outXCheck(x) {
      x = x - this.maskRect.width / 2;
      if (x < 0) {
        return 0;
      }
      if (x + this.maskRect.width > this.imgRect.width) {
        return this.imgRect.width - this.maskRect.width;
      }
      return x;
    },
    outYCheck(y) {
      y = y - this.maskRect.height / 2;
      if (y < 0) {
        return 0;
      }
      if (y + this.maskRect.height > this.imgRect.height) {
        return this.imgRect.height - this.maskRect.height;
      }
      return y;
    }
  }
};
</script>

<style module lang="scss">
@import "./ImageMagnifier.scss";
</style>
